import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Paper,
  Typography,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteOutlineIcon,
  Search as SearchIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import { getCoverArtUrl } from '../../utils/urlHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CuratorLibraryPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    rating: '',
    accessMethod: '',
    favorites: false
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [editDialog, setEditDialog] = useState(false);

  // Fetch library data
  const fetchLibrary = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        sortBy: 'savedAt',
        sortOrder: 'desc'
      });

      // Add filters to params
      if (filters.search) params.append('search', filters.search);
      if (filters.rating) params.append('rating', filters.rating);
      if (filters.accessMethod) params.append('accessMethod', filters.accessMethod);
      if (filters.favorites) params.append('favorites', 'true');

      const response = await axios.get(`${API_URL}/curator/library?${params}`);
      const { libraryEntries, pagination } = response.data;
      
      setLibrary(libraryEntries);
      setTotalPages(pagination.totalPages);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch library');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  // Fetch library statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/curator/library/stats`);
      setStats(response.data);
    } catch (err) {
    }
  };

  useEffect(() => {
    if (user && user.accountType === 'curator') {
      fetchLibrary();
      fetchStats();
    }
  }, [user, fetchLibrary]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  // Handle favorite toggle
  const toggleFavorite = async (entryId, currentFavorite) => {
    try {
      await axios.put(`${API_URL}/curator/library/${entryId}`, {
        isFavorite: !currentFavorite
      });
      fetchLibrary();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update favorite');
    }
  };

  // Handle edit entry
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setEditDialog(true);
  };

  // Save edited entry
  const saveEdit = async () => {
    try {
      await axios.put(`${API_URL}/curator/library/${editingEntry._id}`, {
        notes: editingEntry.notes,
        rating: editingEntry.rating,
        tags: editingEntry.tags
      });
      setEditDialog(false);
      fetchLibrary();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update entry');
    }
  };

  // Remove from library
  const removeFromLibrary = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/curator/library/${entryId}`);
      fetchLibrary();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove from library');
    }
  };

  // Navigate to promo page
  const navigateToPromoPage = (entry) => {
    if (entry.release) {
      // Check if this is a single track release (only one track) or a multi-track release
      if (entry.release.tracks && entry.release.tracks.length === 1) {
        // For single track releases, navigate to the track promo page
        navigate(`/promo/track/${entry.release.tracks[0]._id}`);
      } else {
        // For multi-track releases, we need to check if we have a promo code
        // If we don't have the promo code, we can try to construct it or show an error
        // For now, let's try to get the promo code via the release API
        getPromoCode(entry.release._id);
      }
    }
  };

  // Get promo code for a release
  const getPromoCode = async (releaseId) => {
    try {
      const response = await axios.get(`${API_URL}/releases/${releaseId}`);
      const release = response.data;
      
      if (release.promoCode) {
        navigate(`/promo/release/${release.promoCode}`);
      } else {
        setError('No promo code available for this release');
      }
    } catch (err) {
      setError('Failed to get promo code for this release');
    }
  };

  if (user?.accountType !== 'curator') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          This page is only accessible to curator accounts.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: '#e4e4e4' }}>
          My Library
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your collection of tracks and releases
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {stats.totalEntries || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Items
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {stats.downloadedCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Downloaded
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {stats.favoritesCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Favorites
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {stats.recentActivity || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recent (30d)
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}>
              <InputLabel>Rating</InputLabel>
              <Select
                value={filters.rating}
                label="Rating"
                onChange={(e) => handleFilterChange('rating', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="5">5 Stars</MenuItem>
                <MenuItem value="4">4+ Stars</MenuItem>
                <MenuItem value="3">3+ Stars</MenuItem>
                <MenuItem value="2">2+ Stars</MenuItem>
                <MenuItem value="1">1+ Stars</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}>
              <InputLabel>Access Method</InputLabel>
              <Select
                value={filters.accessMethod}
                label="Access Method"
                onChange={(e) => handleFilterChange('accessMethod', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="promo_link">Promo Link</MenuItem>
                <MenuItem value="comment_download">Comment Download</MenuItem>
                <MenuItem value="public_access">Public Access</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant={filters.favorites ? 'contained' : 'outlined'}
              startIcon={<FavoriteIcon />}
              onClick={() => handleFilterChange('favorites', !filters.favorites)}
            >
              Favorites
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Library Items */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {library.map((entry) => (
            <Grid item xs={12} sm={6} md={4} key={entry._id}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={getCoverArtUrl(entry.release.coverArt)}
                  alt={entry.release.title}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {entry.release.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    by {entry.release.artist.artistName || entry.release.artist.username}
                  </Typography>
                  
                  {entry.track && (
                    <Typography variant="body2" color="primary" gutterBottom>
                      Track: {entry.track.title}
                    </Typography>
                  )}

                  {/* Access Method Chip */}
                  <Chip
                    label={entry.accessMethod.replace('_', ' ').toUpperCase()}
                    size="small"
                    color={entry.accessMethod === 'promo_link' ? 'primary' : 'default'}
                    sx={{ mb: 1 }}
                  />

                  {/* Rating */}
                  {entry.rating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Rating 
                        value={entry.rating} 
                        readOnly 
                        size="small" 
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: '#a187f2',
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        ({entry.rating})
                      </Typography>
                    </Box>
                  )}

                  {/* Notes */}
                  {entry.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      "{entry.notes}"
                    </Typography>
                  )}

                  {/* Tags */}
                  {entry.tags && entry.tags.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                      {entry.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  )}

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <IconButton
                        color={entry.isFavorite ? 'error' : 'default'}
                        onClick={() => toggleFavorite(entry._id, entry.isFavorite)}
                        title="Toggle favorite"
                      >
                        {entry.isFavorite ? <FavoriteIcon /> : <FavoriteOutlineIcon />}
                      </IconButton>
                      <IconButton color="primary" onClick={() => handleEdit(entry)} title="Edit entry">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => removeFromLibrary(entry._id)} title="Remove from library">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LaunchIcon />}
                        onClick={() => navigateToPromoPage(entry)}
                      >
                        Promo Page
                      </Button>
                      {entry.downloaded && (
                        <Chip label="Downloaded" color="success" size="small" />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Library Entry</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={editingEntry?.notes || ''}
            onChange={(e) => setEditingEntry({...editingEntry, notes: e.target.value})}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography component="legend">Rating</Typography>
            <Rating
              value={editingEntry?.rating || 0}
              onChange={(event, newValue) => setEditingEntry({...editingEntry, rating: newValue})}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#a187f2',
                },
                '& .MuiRating-iconHover': {
                  color: '#a187f2',
                }
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Tags (comma separated)"
            value={editingEntry?.tags?.join(', ') || ''}
            onChange={(e) => setEditingEntry({
              ...editingEntry,
              tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
            })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={saveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CuratorLibraryPage;
